import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  InputGroup,
  InputLeftElement,
  Input,
  Icon,
  VStack,
  HStack,
  Text,
  Badge,
  Spinner,
  Center,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSearch, FiClock, FiBriefcase } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import StageBadge from './StageBadge';
import {
  supabase,
  isSupabaseConfigured,
  transformDealToCamelCase,
  transformContactToCamelCase,
} from '../../utils/supabase';
import { applyDealScopeSync, searchContactIdsForTerm } from '../../hooks/useDealsTable';
import { useVisibilityScope } from '../../hooks/useVisibilityScope';
import { applyVisibilityScope, visibilityScopeKey } from '../../utils/recordVisibility';

// Minimal payload for result rows (vs. select * on the full 80+ column table)
const DEAL_SELECT =
  'id, deal_name, stage, amount, arive_id, loan_number, owner_id, updated_at, contact_id, crm_contacts!contact_id(id, first_name, last_name, email, phone)';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_CHARS = 2;

// Commas and parens are PostgREST or() syntax — strip them so a term like
// "Smith, John" can't produce a malformed filter string.
function sanitizeSearchTerm(term) {
  return term.replace(/[,()]/g, ' ').replace(/\s+/g, ' ').trim();
}

function mapDealRow(row) {
  const deal = transformDealToCamelCase(row);
  if (row.crm_contacts) {
    deal.contact = transformContactToCamelCase(row.crm_contacts);
  }
  return deal;
}

/**
 * Quick-search modal for hopping between deals without leaving the deal page.
 *
 * - Empty input shows the user's recently viewed deals (hydrated fresh from
 *   Supabase so deleted/reassigned deals silently drop out).
 * - Typing searches deal name, lender loan #, Arive ID, and the linked
 *   contact's name/email/phone (phone is format-agnostic).
 * - Non-admins are scoped to their own deals; CRM Admins / SuperAdmins search
 *   everyone's and see the owner on each row.
 */
export default function DealQuickSearchModal({
  isOpen,
  onClose,
  currentDealId = null,
  isCRMAdmin = false,
  crmUsers = [],
  recentViews = [],
}) {
  const { currentUser } = useAuth();
  const history = useHistory();
  const inputRef = useRef(null);
  const highlightedRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recentDeals, setRecentDeals] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const uid = currentUser?.uid;
  const sanitizedTerm = sanitizeSearchTerm(searchTerm);
  const isSearchMode = sanitizedTerm.length >= MIN_SEARCH_CHARS;

  // Multi-owner visibility: admins search everyone; everyone else searches
  // their effective book (owned + team book-shares + record shares).
  const { scope: dealVisibilityScope } = useVisibilityScope({
    entityType: 'deal',
    targetUserId: isCRMAdmin ? null : uid,
  });
  const dealScopeKey = visibilityScopeKey(dealVisibilityScope) + ':g' + (dealVisibilityScope?.grantsVersion || 0);

  // Deal you're already on is excluded — hopping "back" to it is a no-op.
  const recentIds = useMemo(
    () =>
      (recentViews || [])
        .map((view) => view?.dealId)
        .filter((id) => id && id !== currentDealId),
    [recentViews, currentDealId]
  );

  // Reset transient state each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSearchResults([]);
      setSearching(false);
      setHighlightIndex(0);
    }
  }, [isOpen]);

  // Hydrate recently viewed deals when the modal opens. The owner filter (not
  // the stored history) is what keeps non-admins from seeing deals that were
  // reassigned away from them.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    const loadRecent = async () => {
      if (!isSupabaseConfigured() || recentIds.length === 0) {
        setRecentDeals([]);
        setLoadingRecent(false);
        return;
      }
      setLoadingRecent(true);
      try {
        let query = supabase.from('crm_deals').select(DEAL_SELECT).in('id', recentIds);
        // Visibility (not the stored history) keeps non-admins from seeing
        // deals that were reassigned away — now including shared books.
        query = applyVisibilityScope(query, dealVisibilityScope);
        const { data, error } = await query;
        if (cancelled) return;
        if (error) {
          console.error('DealQuickSearch: failed to load recent deals', error);
          setRecentDeals([]);
        } else {
          const byId = new Map((data || []).map((row) => [row.id, row]));
          // .in() doesn't preserve order — restore stored recency order
          setRecentDeals(
            recentIds
              .map((id) => byId.get(id))
              .filter(Boolean)
              .map(mapDealRow)
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error('DealQuickSearch: failed to load recent deals', error);
          setRecentDeals([]);
        }
      } finally {
        if (!cancelled) setLoadingRecent(false);
      }
    };

    loadRecent();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recentIds, dealScopeKey]);

  // Debounced search across deal fields + linked contact fields
  useEffect(() => {
    if (!isOpen) return undefined;
    if (!isSearchMode) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    setSearching(true);

    const timer = setTimeout(async () => {
      if (!isSupabaseConfigured()) {
        if (!cancelled) setSearching(false);
        return;
      }
      try {
        const contactIds = await searchContactIdsForTerm(sanitizedTerm);
        if (cancelled) return;

        let query = supabase.from('crm_deals').select(DEAL_SELECT);
        query = applyDealScopeSync(query, {
          ownerScope: dealVisibilityScope,
          searchTerm: sanitizedTerm,
          contactIds,
        });
        const { data, error } = await query
          .order('updated_at', { ascending: false })
          .limit(20);
        if (cancelled) return;

        if (error) {
          console.error('DealQuickSearch: search failed', error);
          setSearchResults([]);
        } else {
          setSearchResults((data || []).map(mapDealRow));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('DealQuickSearch: search failed', error);
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSearchMode, sanitizedTerm, dealScopeKey]);

  const displayedDeals = isSearchMode ? searchResults : recentDeals;
  const displayedLoading = isSearchMode ? searching : loadingRecent;

  // Keep the highlight in range as the list changes
  useEffect(() => {
    setHighlightIndex(0);
  }, [isSearchMode, searchResults, recentDeals]);

  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const handleSelect = (deal) => {
    onClose();
    if (deal?.id && deal.id !== currentDealId) {
      history.push(`/crm/deals/${deal.id}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, Math.max(displayedDeals.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = displayedDeals[highlightIndex] || displayedDeals[0];
      if (target) handleSelect(target);
    }
  };

  const ownerNameFor = (ownerId) =>
    (crmUsers || []).find((user) => user.uid === ownerId)?.name || null;

  const renderDealRow = (deal, index) => {
    const isHighlighted = index === highlightIndex;
    const contact = deal.contact;
    const contactName = contact
      ? [contact.firstName, contact.lastName].filter(Boolean).join(' ')
      : '';
    const ownerName = isCRMAdmin ? ownerNameFor(deal.ownerId) : null;

    return (
      <Box
        key={deal.id}
        ref={isHighlighted ? highlightedRef : undefined}
        p={3}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
        bg={isHighlighted ? highlightBg : undefined}
        _hover={{ bg: isHighlighted ? highlightBg : hoverBg, cursor: 'pointer' }}
        onClick={() => handleSelect(deal)}
        onMouseEnter={() => setHighlightIndex(index)}
      >
        <HStack spacing={3} align="flex-start">
          <Icon as={FiBriefcase} boxSize={5} color="blue.500" mt={0.5} />
          <VStack align="flex-start" spacing={1} flex={1} minW={0}>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                {deal.dealName || 'Unnamed Deal'}
              </Text>
              <StageBadge stage={deal.stage} />
              {ownerName && (
                <Badge colorScheme="purple" fontSize="2xs">
                  {ownerName}
                </Badge>
              )}
            </HStack>
            <HStack spacing={3} flexWrap="wrap">
              {contactName && (
                <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                  {contactName}
                </Text>
              )}
              {(contact?.email || contact?.phone) && (
                <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                  {contact.email || contact.phone}
                </Text>
              )}
            </HStack>
            {(deal.loanNumber || deal.ariveId) && (
              <HStack spacing={3} flexWrap="wrap">
                {deal.loanNumber && (
                  <Text fontSize="xs" color={mutedColor}>
                    Loan #{deal.loanNumber}
                  </Text>
                )}
                {deal.ariveId && (
                  <Text fontSize="xs" color={mutedColor}>
                    Arive ID {deal.ariveId}
                  </Text>
                )}
              </HStack>
            )}
          </VStack>
        </HStack>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" initialFocusRef={inputRef}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Find a Deal</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          <VStack spacing={4} align="stretch">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                ref={inputRef}
                placeholder="Search by name, phone, email, Arive ID, or loan #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </InputGroup>

            {!isSearchMode && (
              <HStack spacing={2}>
                <Icon as={FiClock} boxSize={3.5} color={mutedColor} />
                <Text fontSize="xs" fontWeight="semibold" color={mutedColor} textTransform="uppercase">
                  Recently Viewed
                </Text>
              </HStack>
            )}

            <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
              {displayedLoading ? (
                <Center py={8}>
                  <Spinner size="sm" />
                </Center>
              ) : searchTerm.trim() && !isSearchMode ? (
                <Center py={8}>
                  <Text color="gray.500" fontSize="sm">
                    Type at least {MIN_SEARCH_CHARS} characters to search...
                  </Text>
                </Center>
              ) : displayedDeals.length > 0 ? (
                displayedDeals.map((deal, index) => renderDealRow(deal, index))
              ) : isSearchMode ? (
                <Center py={8}>
                  <Text color="gray.500" fontSize="sm">
                    No deals found
                  </Text>
                </Center>
              ) : (
                <Center py={8}>
                  <Text color="gray.500" fontSize="sm" textAlign="center">
                    No recently viewed deals yet — deals you open will show up here.
                  </Text>
                </Center>
              )}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
